import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
  type Dispatch,
  type SetStateAction,
} from 'react'
import { Edit, Visibility, VisibilityOff } from '@mui/icons-material'

// Context
import { useUser } from 'context/user/user.context'

// Components
import { TextArea, Button, Input } from 'components'

import { translate } from 'locales/index'
import { alertSuccess, alertDanger } from 'utils/toast'

// Styles
import defaultImg from 'assets/images/avatar.jpg'
import aiIcon from 'assets/icons/mage_stars-c.svg'
import * as S from './styles'
import api from 'configs/api'

// Services
import {
  updateProfileBio,
  findUserData,
  uploadProfilePhoto,
  generateBioWithAI,
  getProfileBio,
  generatePictureWithAI,
  updatePassword,
} from './services'

const maxPhotoBytes = 4 * 2048 * 2048
const acceptImageTypes = [
  'image/jpeg',
  'image/pjpeg',
  'image/png',
  'image/gif',
  'image/webp',
]

const validatePhotoFile = (file: File) => {
  if (file.size > maxPhotoBytes) {
    alertDanger(translate('profile.fileSize'))
    return false
  }
  if (!acceptImageTypes.includes(file.type)) {
    alertDanger(translate('profile.invalidFile'))
    return false
  }
  return true
}

const mimeToExtension: Record<string, string> = {
  'image/png': 'png',
  'image/gif': 'gif',
  'image/webp': 'webp',
}

const resolveImageMime = (blob: Blob, contentType?: string | null) => {
  if (blob.type?.startsWith('image/')) return blob.type
  const headerMime = contentType?.split(';')[0]?.trim()
  return headerMime?.startsWith('image/') ? headerMime : 'image/jpeg'
}

const blobToImageFile = (blob: Blob, contentType?: string | null) => {
  const mime = resolveImageMime(blob, contentType)
  const extension = mimeToExtension[mime] ?? 'jpg'
  return new File([blob], `photo.${extension}`, { type: mime })
}

const noopSetLoading: Dispatch<SetStateAction<boolean>> = () => undefined

const Photo: React.FunctionComponent<{ setLoading?: any }> = ({
  setLoading,
}) => {
  const { userData, setUserData } = useUser()
  const [bio, setBio] = useState('')
  const [loadingAIPicture, setLoadingAIPicture] = useState(false)
  const [loadingAI, setLoadingAI] = useState(false)
  const [isAIGenerated, setIsAIGenerated] = useState(false)
  const [professionalInfo, setProfessionalInfo] = useState('')
  const [photoModalOpen, setPhotoModalOpen] = useState(false)
  const [selectedPhotoFile, setSelectedPhotoFile] = useState<File | null>(null)
  const [canUseAIPicture, setCanUseAIPicture] = useState(false)
  const [localPhotoPreview, setLocalPhotoPreview] = useState<string | null>(
    null
  )
  const [savingPhoto, setSavingPhoto] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [newPassword, setNewPassword] = useState('')
  const [newPasswordConfirmation, setNewPasswordConfirmation] = useState('')
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showNewPasswordConfirmation, setShowNewPasswordConfirmation] =
    useState(false)
  const [savingPassword, setSavingPassword] = useState(false)

  const passwordLength = useMemo(() => {
    return newPassword ? newPassword.length >= 8 : false
  }, [newPassword])

  const passwordHasNumber = useMemo(() => {
    const regex = /\d/
    return regex.test(newPassword || '')
  }, [newPassword])

  const passwordHasUpperCaseLetter = useMemo(() => {
    const regex = /[A-Z]/
    return regex.test(newPassword || '')
  }, [newPassword])

  const passwordHasLowerCaseLetter = useMemo(() => {
    const regex = /[a-z]/
    return regex.test(newPassword || '')
  }, [newPassword])

  const passwordIsValid = useMemo(() => {
    const regexPassword = /(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])/
    return (
      newPassword.length >= 8 &&
      newPassword === newPasswordConfirmation &&
      regexPassword.test(newPassword)
    )
  }, [newPassword, newPasswordConfirmation])

  const handleSavePassword = async () => {
    if (!passwordIsValid) return
    await updatePassword(
      newPassword,
      userData.id,
      setSavingPassword,
      setNewPassword,
      setNewPasswordConfirmation
    )
  }

  const revokeLocalPreview = useCallback(() => {
    if (localPhotoPreview) {
      URL.revokeObjectURL(localPhotoPreview)
    }
  }, [localPhotoPreview])

  const resetPhotoModal = useCallback(() => {
    revokeLocalPreview()
    setLocalPhotoPreview(null)
    setSelectedPhotoFile(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [revokeLocalPreview])

  const openPhotoModal = useCallback(() => {
    resetPhotoModal()
    setPhotoModalOpen(true)
  }, [resetPhotoModal])

  const closePhotoModal = useCallback(() => {
    resetPhotoModal()
    setPhotoModalOpen(false)
  }, [resetPhotoModal])

  const handleModalFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!validatePhotoFile(file)) {
      e.target.value = ''
      return
    }
    revokeLocalPreview()
    setLocalPhotoPreview(URL.createObjectURL(file))
    setSelectedPhotoFile(file)
  }

  const handleSavePhotoFromModal = async () => {
    if (!selectedPhotoFile) return
    if (setLoading) setLoading(true)
    setSavingPhoto(true)
    const doctorId = userData?.id
    try {
      const usedAIPicture =
        canUseAIPicture || userData?.profile?.photo_ia_generated || false

      await uploadProfilePhoto(selectedPhotoFile, doctorId, usedAIPicture)
      await findUserData(setUserData, setLoading ?? noopSetLoading)
      alertSuccess(translate('profile.updateFileSuccess'))
      closePhotoModal()
    } catch {
      if (setLoading) setLoading(false)
      alertDanger(translate('profile.updateFileError'))
    } finally {
      setSavingPhoto(false)
    }
  }

  const handleEditPhotoClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    openPhotoModal()
  }

  const handleImageClick = () => {
    openPhotoModal()
  }

  const handleBioChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value
    setBio(newValue)
    if (isAIGenerated) {
      setIsAIGenerated(false)
    }
  }

  const handleSaveBio = async () => {
    if (setLoading) setLoading(true)
    try {
      await updateProfileBio(bio, userData.id, isAIGenerated)
      setUserData({
        ...userData,
        profile: {
          ...userData.profile,
          bio,
        },
      })
      setIsAIGenerated(false)
      alertSuccess('Bio atualizada com sucesso!')
    } catch (error: any) {
      alertDanger(error?.response?.data?.message || 'Erro ao atualizar bio')
    } finally {
      if (setLoading) setLoading(false)
    }
  }

  const handleAISuggestions = async () => {
    setLoadingAI(true)
    try {
      const generatedBio = await generateBioWithAI(professionalInfo)
      setBio(generatedBio)
      setIsAIGenerated(true)
      alertSuccess('Bio gerada com sucesso pela IA!')
    } catch (error: any) {
      alertDanger(
        error?.message || 'Erro ao gerar sugestão de bio. Tente novamente.'
      )
    } finally {
      setLoadingAI(false)
    }
  }

  const resolveImageBlobForAI = async (): Promise<File> => {
    if (selectedPhotoFile) return selectedPhotoFile

    if (localPhotoPreview) {
      const res = await fetch(
        localPhotoPreview || userData.profile.photo || defaultImg
      )
      if (!res.ok) {
        throw new Error('Não foi possível carregar a foto para envio.')
      }
      return blobToImageFile(await res.blob(), res.headers.get('content-type'))
    }

    if (userData.profile.photo) {
      const key = userData.profile.photo.split('.com/')[1]
      const { data, headers } = await api.get<Blob>(
        `/files/file/get?key=${key}&isPublic=true&raw=true`,
        { responseType: 'blob' }
      )
      const contentType = headers['content-type']
      return blobToImageFile(
        data,
        typeof contentType === 'string' ? contentType : undefined
      )
    }

    throw new Error('Não foi possível carregar a foto para envio.')
  }

  const handleAIPictureSuggestion = async () => {
    if (!userData?.profile?.photo_ia_generated) {
      setCanUseAIPicture(true)
    }
    setLoadingAIPicture(true)
    try {
      const imageFile = await resolveImageBlobForAI()
      const file = await generatePictureWithAI(imageFile)
      if (!validatePhotoFile(file)) {
        return
      }
      if (localPhotoPreview?.startsWith('blob:')) {
        URL.revokeObjectURL(localPhotoPreview)
      }
      const previewUrl = URL.createObjectURL(file)
      setLocalPhotoPreview(previewUrl)
      setSelectedPhotoFile(file)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      alertSuccess('Foto gerada com sucesso pela IA!')
    } catch (error: any) {
      alertDanger(
        error?.message || 'Erro ao gerar sugestão de foto. Tente novamente.'
      )
    } finally {
      setLoadingAIPicture(false)
    }
  }

  useEffect(() => {
    const loadBio = async () => {
      if (userData?.id) {
        try {
          const existingBio = await getProfileBio(userData.id)
          if (existingBio) {
            setBio(existingBio)
          }
        } catch (error) {
          console.error('Erro ao carregar bio:', error)
        }
      }
    }

    loadBio()
  }, [userData?.id])

  useEffect(
    () => () => {
      if (localPhotoPreview?.startsWith('blob:')) {
        URL.revokeObjectURL(localPhotoPreview)
      }
    },
    [localPhotoPreview]
  )

  useEffect(() => {
    if (!photoModalOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closePhotoModal()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [photoModalOpen, closePhotoModal])

  const currentPhoto = userData.profile.photo || defaultImg
  const modalPreviewUrl = localPhotoPreview || currentPhoto

  const modalPhoto = () => (
    <S.PhotoModalRoot
      role="dialog"
      aria-modal
      aria-label={String(translate('profile.photo'))}
      onClick={closePhotoModal}
    >
      <S.PhotoModalBackdrop />
      <S.PhotoEditModal onClick={(e) => e.stopPropagation()}>
        <div className="section-header">
          <h3>{translate('profile.editPhoto')}</h3>
        </div>
        <div className="modal-circle-wrap">
          <img src={modalPreviewUrl} alt={userData.name} />
        </div>
        <div className="ai-photo-button-container">
          <S.AISuggestionsButton
            type="button"
            className="ai-photo-button"
            onClick={handleAIPictureSuggestion}
            disabled={
              loadingAIPicture ||
              loadingAI ||
              userData?.profile?.photo_ia_generated
            }
          >
            {loadingAIPicture
              ? translate('profile.generating')
              : translate('profile.suggestionWithAI')}
            <img src={aiIcon} alt="IA" />
          </S.AISuggestionsButton>
        </div>

        <input
          type="file"
          accept="image/jpeg,image/pjpeg,image/png,image/gif,image/webp"
          ref={fileInputRef}
          onChange={handleModalFileChange}
          style={{ display: 'none' }}
        />
        <div className="section-info">
          <h5>
            O recurso de sugestão com IA pode ser utilizado apenas uma única
            vez. Certifique-se de que selecionou o arquivo correto antes de
            continuar!
          </h5>
        </div>
        <div className="modal-button-row">
          <Button
            type="button"
            color="warning"
            size="large"
            outline
            onClick={closePhotoModal}
          >
            {translate('profile.cancel')}
          </Button>
          <Button
            type="button"
            color="warning"
            size="large"
            outline
            onClick={() => fileInputRef.current?.click()}
            disabled={loadingAIPicture || loadingAI}
          >
            {translate('profile.changePhoto')}
          </Button>
          <Button
            type="button"
            color="warning"
            size="large"
            onClick={handleSavePhotoFromModal}
            disabled={
              !selectedPhotoFile || savingPhoto || loadingAIPicture || loadingAI
            }
          >
            {translate('profile.save')}
          </Button>
        </div>
      </S.PhotoEditModal>
    </S.PhotoModalRoot>
  )

  return (
    <S.PhotoProfile>
      {photoModalOpen && modalPhoto()}
      <div className="photo-section">
        <div className="section-header">
          <h3>{translate('profile.photo')}</h3>
        </div>
        <div className="content-photo">
          <div className="photo-content-wrapper">
            <img
              src={currentPhoto}
              alt={userData.name}
              onClick={handleImageClick}
              style={{ cursor: 'pointer' }}
            />
            <button
              className="edit-photo-button"
              type="button"
              onClick={handleEditPhotoClick}
            >
              <Edit style={{ fontSize: 16, color: '#fff' }} />
            </button>
          </div>
        </div>
      </div>

      <div className="bio-section">
        <div className="section-header">
          <h3>{translate('profile.bio') || 'Bio'}</h3>
        </div>

        <S.ProfessionalInfoContainer>
          <S.ProfessionalInfoWrapper>
            <h4>{translate('profile.professionalInfoLabel')}</h4>
            <Input
              name="professionalInfo"
              placeholder="Insira aqui"
              value={professionalInfo}
              type="text"
              onChange={(e) => setProfessionalInfo(e.target.value)}
            />
          </S.ProfessionalInfoWrapper>
          <S.AISuggestionsButton
            onClick={handleAISuggestions}
            type="button"
            disabled={loadingAI || !professionalInfo.trim()}
          >
            <img src={aiIcon} alt="IA" />
            {loadingAI
              ? translate('profile.generating')
              : translate('profile.aiSuggestions')}
          </S.AISuggestionsButton>
        </S.ProfessionalInfoContainer>

        <S.BioTextArea>
          <TextArea
            name="bio"
            placeholder="Escreva aqui..."
            value={bio}
            onChange={handleBioChange}
            rows={3}
          />
        </S.BioTextArea>
        <div className="bio-actions">
          <Button
            type="button"
            color="primary"
            size="small"
            onClick={handleSaveBio}
            disabled={bio === (userData?.profile?.bio || '')}
          >
            {translate('profile.save') || 'Salvar'}
          </Button>
        </div>
      </div>

      <div className="password-section">
        <div className="section-header">
          <h3>{translate('profile.passwordSectionTitle')}</h3>
        </div>
        <p className="password-hint">
          {translate('profile.passwordSectionHint')}
        </p>

        <div className="content-input">
          <h4>{translate('profile.newPassword')}</h4>
          <div className="password-input-wrapper">
            <Input
              name="novaSenha"
              placeholder={translate('profile.newPassword')}
              maxLength={15}
              value={newPassword}
              type={showNewPassword ? 'text' : 'password'}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <button
              className="button-show-password"
              type="button"
              onClick={() => setShowNewPassword(!showNewPassword)}
            >
              {showNewPassword ? <VisibilityOff /> : <Visibility />}
            </button>
          </div>
        </div>
        <div className="content-input">
          <h4>{translate('profile.confirmNewPassword')}</h4>
          <div className="password-input-wrapper">
            <Input
              name="confirmarNovaSenha"
              placeholder={translate('profile.confirmNewPassword')}
              maxLength={15}
              value={newPasswordConfirmation}
              type={showNewPasswordConfirmation ? 'text' : 'password'}
              onChange={(e) => setNewPasswordConfirmation(e.target.value)}
              disabled={!newPassword}
            />
            <button
              className="button-show-password"
              type="button"
              onClick={() =>
                setShowNewPasswordConfirmation(!showNewPasswordConfirmation)
              }
            >
              {showNewPasswordConfirmation ? <VisibilityOff /> : <Visibility />}
            </button>
          </div>
        </div>

        {newPassword && (
          <div className="new-password-demands">
            <p>{translate('profile.passwordRules.one')}</p>
            <ul>
              <li style={{ color: passwordLength ? 'green' : 'red' }}>
                {translate('profile.passwordRules.two')}
              </li>
              <li
                style={{ color: passwordHasUpperCaseLetter ? 'green' : 'red' }}
              >
                {translate('profile.passwordRules.three')}
              </li>
              <li
                style={{ color: passwordHasLowerCaseLetter ? 'green' : 'red' }}
              >
                {translate('profile.passwordRules.four')}
              </li>
              <li style={{ color: passwordHasNumber ? 'green' : 'red' }}>
                {translate('profile.passwordRules.five')}
              </li>
            </ul>
          </div>
        )}

        <div className="password-actions">
          <Button
            type="button"
            color="primary"
            size="small"
            onClick={handleSavePassword}
            disabled={!passwordIsValid || savingPassword}
          >
            {translate('profile.save')}
          </Button>
        </div>
      </div>
    </S.PhotoProfile>
  )
}

export default Photo
